import CloseIcon from '@mui/icons-material/Close';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { Box, Grid, IconButton, List } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { LuGlobe } from 'react-icons/lu';
import { ILeadSearchFilter } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsTypography,
  WsTypoVariants,
  BuildingIcon,
  WsTypoColors,
  UserTRoundCheckIcon,
  WsAutocompleteInput,
  UserRoundIcon,
  TagsIcon,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import { ContactAutocomplete } from '../../../components/ContactAutocomplete';
import {
  CreatedBwDatesFilter,
  WsAccordion,
  WsTagsFilter,
} from '../../../components/filters/components';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { defaultLeadSearchFilter, leadFilterDateOptions } from '../helper/leadHelper';

export interface ILeadsAdvancedFiltersProps {
  onClose: () => void;
  onSubmit: (fields: ILeadSearchFilter) => void;
  defaultValues: ILeadSearchFilter;
  tags: string[];
}

const LeadAdvanceFilters = ({
  onClose,
  onSubmit,
  defaultValues,
  tags,
}: ILeadsAdvancedFiltersProps) => {
  const methods = useForm<ILeadSearchFilter>({
    defaultValues,
  });

  const { control, handleSubmit, reset, getValues, setValue, watch } = methods;
  const [resetKey, setResetKey] = useState(0);

  const leadOwnersOperator = useWatch({ control, name: 'leadOwnersOperator' }) ?? 'is';
  const createdByOperator = useWatch({ control, name: 'createdByOperator' }) ?? 'is';
  const clientOperator = useWatch({ control, name: 'clientOperator' }) ?? 'is';
  const contactOperator = useWatch({ control, name: 'contactOperator' }) ?? 'is';
  const sourceOperator = useWatch({ control, name: 'sourceOperator' }) ?? 'is';
  const tagsOperator = useWatch({ control, name: 'tagsOperator' }) ?? 'is';

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
      setResetKey((prev) => prev + 1);
    }
  }, [defaultValues, reset]);

  const handleOperatorSelect =
    (field: keyof ILeadSearchFilter) => (option: { label: string; value: string }) =>
      setValue(field, option.value);

  const handleReset = () => {
    reset();
    onSubmit(defaultLeadSearchFilter);
  };

  const shouldRenderField = (operator: string) => !['is_blank', 'is_not_blank'].includes(operator);

  return (
    <FormProvider {...methods}>
      <Box
        key={resetKey}
        component="form"
        role="presentation"
        onSubmit={handleSubmit(onSubmit)}
        onReset={handleReset}
        bgcolor={WsColors.White}
        borderRadius={2}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          p={1}
          borderRadius={2}
          alignItems="center"
        >
          <WsTypography variant={WsTypoVariants.TITLE} bold>
            Advanced Filters
          </WsTypography>
          <WsButton className="advance-filter" variant="contained" size="small" type="submit">
            Apply
          </WsButton>
          <IconButton type="reset">
            <RestartAltOutlinedIcon color="error" />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon color="disabled" />
          </IconButton>
        </Grid>
        <List>
          <Box p={1} ml={1}>
            <WsTypography color={WsTypoColors.PRIMARY} variant={WsTypoVariants.SUB_TITLE} bold>
              Leads
            </WsTypography>
          </Box>
          <CreatedBwDatesFilter
            title="Created or Updated between"
            name="fromDate"
            name1="toDate"
            selectStatus="filterByDateType"
            disableFuture={false}
            showAccordionIcon
            isSubmittedOrUpdated
            selectArray={leadFilterDateOptions}
          />
          <WsAccordion
            title="Created By"
            startIcon={<UserRoundIcon />}
            titleColor={WsTypoColors.GREYTEXT}
            showOperatorDropdown
            handleOperatorSelect={handleOperatorSelect('createdByOperator')}
            defaultOperatorValue={getValues('createdByOperator')}
          >
            {shouldRenderField(createdByOperator) && (
              <RecruiterSuggestions
                name="createdByIds"
                multiple
                defaultValue={getValues('createdByIds')}
                value={watch('createdByIds')}
              />
            )}
          </WsAccordion>
          <WsAccordion
            title="Lead Owner"
            startIcon={<UserRoundIcon />}
            titleColor={WsTypoColors.GREYTEXT}
            showOperatorDropdown
            handleOperatorSelect={handleOperatorSelect('leadOwnersOperator')}
            defaultOperatorValue={getValues('leadOwnersOperator')}
          >
            {shouldRenderField(leadOwnersOperator) && (
              <RecruiterSuggestions
                name="leadOwners"
                multiple={true}
                defaultValue={getValues('leadOwners')}
                value={watch('leadOwners')}
              />
            )}
          </WsAccordion>
          <WsAccordion
            title="Client Name(s)"
            startIcon={<BuildingIcon />}
            titleColor={WsTypoColors.GREYTEXT}
            showOperatorDropdown
            handleOperatorSelect={handleOperatorSelect('clientOperator')}
            defaultOperatorValue={getValues('clientOperator')}
          >
            {shouldRenderField(clientOperator) && <ClientAutocomplete name="clientIds" multiple />}
          </WsAccordion>
          <WsAccordion
            title="Client Contact(s)"
            startIcon={<UserTRoundCheckIcon />}
            titleColor={WsTypoColors.GREYTEXT}
            showOperatorDropdown
            handleOperatorSelect={handleOperatorSelect('contactOperator')}
            defaultOperatorValue={getValues('contactOperator')}
          >
            {shouldRenderField(contactOperator) && (
              <ContactAutocomplete name="contactIds" multiple />
            )}
          </WsAccordion>
          <WsAccordion
            title="Source"
            startIcon={<LuGlobe color={WsColors.TertiaryShades800} size={20} />}
            titleColor={WsTypoColors.GREYTEXT}
            showOperatorDropdown
            handleOperatorSelect={handleOperatorSelect('sourceOperator')}
            defaultOperatorValue={getValues('sourceOperator')}
          >
            {shouldRenderField(sourceOperator) && (
              <WsAutocompleteInput
                placeholder="Eg, Michael"
                options={[]}
                multiple
                freeSolo
                name="source"
              />
            )}
          </WsAccordion>
          <WsAccordion
            title="Tags"
            startIcon={<TagsIcon />}
            titleColor={WsTypoColors.GREYTEXT}
            showOperatorDropdown
            handleOperatorSelect={handleOperatorSelect('tagsOperator')}
            defaultOperatorValue={getValues('tagsOperator')}
          >
            {shouldRenderField(tagsOperator) && (
              <WsTagsFilter name="tags" defaultValue={defaultValues.tags} label="" options={tags} />
            )}
          </WsAccordion>
        </List>
      </Box>
    </FormProvider>
  );
};

export default LeadAdvanceFilters;
